import { DocumentToGenerate, ModelType, PrintableDocumentType } from '@livv/models';
import { AnalysisDisplayPublicWithPremiumStatus } from '@livv/models/firebaseFunctions';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    SxProps,
    Typography,
} from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import AiSearchFields from './AiSearchFields';
import PrintDecisionFields from './DecisionFields';
import { BaseForm } from '@components/form';

export type PersonalizedDocumentValues = Omit<DocumentToGenerate, 'id' | 'type'>;

const iconSx: SxProps = {
    color: 'grey.100',
    position: 'absolute',
    right: 8,
    top: 8,
};
const titleSx: SxProps = {
    borderBottom: 1,
    borderColor: 'grey.600',
    mb: 2,
    py: 4,
};

interface FormValues extends PersonalizedDocumentValues {
    addNote: boolean;
    linkAnalyses: boolean;
    withAssociatedDocuments?: boolean;
    withFollowupQuestions?: boolean;
}

const INITIAL_VALUES_DECISION: FormValues = {
    addNote: false,
    displaySameCaseDecisions: false,
    linkAnalyses: false,
    linkedAnalyses: [],
    note: '',
};

const INITIAL_VALUES_AI_SEARCH: FormValues = {
    addNote: false,
    displaySameCaseDecisions: false,
    linkAnalyses: false,
    linkedAnalyses: [],
    note: '',
    withAssociatedDocuments: false,
    withFollowupQuestions: false,
};

interface PrintDecisionModalProps {
    analysesWithPremiumStatus?: AnalysisDisplayPublicWithPremiumStatus;
    documentType: PrintableDocumentType;
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    handleSubmit: (formValues: PersonalizedDocumentValues) => void;
}

const PrintModal: FC<PrintDecisionModalProps> = ({
    analysesWithPremiumStatus,
    documentType,
    isOpen,
    setIsOpen,
    handleSubmit,
}) => {
    const { t } = useTranslation('common');

    const { analyses, isPremiumContent } = analysesWithPremiumStatus || {
        analyses: [],
        isPremiumContent: false,
    };

    const analysesOptionsGenerator = () =>
        analyses.reduce((acc, analysisPublicDisplay) => {
            const { analysis: { id } = {}, pathKeywords } = analysisPublicDisplay;
            if (!id) return acc;

            return {
                ...acc,
                [id]: pathKeywords.at(-1),
            };
        }, {});

    // Memoize analyses options to keep a stable reference across re-renders
    const analysesOptions = useMemo(
        () => (isPremiumContent ? analysesOptionsGenerator() : {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isPremiumContent, analyses],
    );

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleFormSubmit = ({
        addNote,
        displaySameCaseDecisions,
        linkAnalyses,
        linkedAnalyses,
        note,
        withAssociatedDocuments,
        withFollowupQuestions,
    }: FieldValues) => {
        const submitValues: PersonalizedDocumentValues = {};

        if (documentType === ModelType.DECISION) {
            submitValues.displaySameCaseDecisions = displaySameCaseDecisions;
            submitValues.linkedAnalyses = linkAnalyses ? linkedAnalyses : undefined;
            submitValues.note = addNote && note ? note : undefined;
        } else if (documentType === 'AiSearch') {
            submitValues.withAssociatedDocuments = withAssociatedDocuments;
            submitValues.withFollowupQuestions = withFollowupQuestions;
        }

        handleSubmit(submitValues);
    };

    const isDecisionType = documentType === ModelType.DECISION;
    // Memoize initial values to prevent BaseForm from resetting on every re-render.
    // For Decision, include derived linkedAnalyses once; for AiSearch, reuse the stable constant.
    const initialValues = useMemo(
        () =>
            isDecisionType
                ? { ...INITIAL_VALUES_DECISION, linkedAnalyses: Object.keys(analysesOptions) }
                : INITIAL_VALUES_AI_SEARCH,
        [isDecisionType, analysesOptions],
    );
    const shouldShowModal = isDecisionType ? isPremiumContent && isOpen : isOpen;

    return (
        <Dialog
            PaperProps={{
                sx: {
                    minWidth: { sm: '600px', xs: '75%' },
                },
            }}
            onClose={handleClose}
            open={shouldShowModal}
        >
            <DialogContent sx={{ pb: 0, pt: 3, px: 3 }}>
                <Box sx={titleSx}>
                    <IconButton aria-label="close" onClick={handleClose} sx={iconSx}>
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h3">{t('print.decisionModal.title')}</Typography>
                    <Typography variant="body1">{t('print.decisionModal.subtitle')}</Typography>
                </Box>
                <BaseForm
                    Fields={isDecisionType ? PrintDecisionFields : AiSearchFields}
                    initialValues={initialValues}
                    name="print-form"
                    onSubmit={handleFormSubmit}
                    props={isDecisionType ? { analysesOptions } : {}}
                    hideSubmit
                />
            </DialogContent>
            <DialogActions sx={{ pb: 3, pt: 2, px: 3 }}>
                <Button form="print-form" type="submit" variant="contained" autoFocus fullWidth>
                    {t('print.decisionModal.export')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrintModal;
